import { z } from "zod";
import { BLOCK_OPS_MAX, BlockKindSchema, BlockOpSchema } from "../blocks/schema.js";
import { ENGINES } from "../catalog-sidecar.js";
import { AGENT_YAML_MAX_BYTES, OVERLAY_YAML_MAX_BYTES, ProjectSidecarSchema } from "../projects.js";
import { ENGINE_TARGETS, RuntimeProviderSchema } from "../runtime.js";
import { PreflightAgentSchema, PreflightFindingSchema, PreflightProfileSchema, PreflightUncheckedSchema, RUN_PREFLIGHT_VERDICTS, RunNeedsSchema, } from "./preflight.js";
// projects.validate (design h4 §B.5, plan row H.36): a candidate's documents checked on every
// provider and runner this deployment serves, without a revision written or a provider called.
// Everything below is whitelisted as the preflight is: codes, ids, counts and fixed sentences
// from V5's own code; never a prompt, a credential, an endpoint or an MCP url.
/** The most edit operations one call applies (projects.applyBlocks takes the same). */
/**
 * What is checked: the base revision after edit operations (`@mfa/v5-contracts/blocks`
 * BlockOp, applied as projects.applyBlocks would), or documents given outright, each missing
 * one taken from the base. No candidate checks the base revision itself.
 */
export const ProjectValidateCandidateSchema = z.union([
    z.object({ ops: z.array(BlockOpSchema).min(1).max(BLOCK_OPS_MAX) }).strict(),
    z
        .object({
        agentYaml: z.string().min(1).max(AGENT_YAML_MAX_BYTES).optional(),
        overlayYaml: z.string().max(OVERLAY_YAML_MAX_BYTES).nullable().optional(),
        sidecar: ProjectSidecarSchema.optional(),
    })
        .strict(),
]);
export const ProjectValidateInputSchema = z.object({
    projectId: z.string().uuid(),
    /** The revision the candidate is built on (the one the caller read); absent = the current one. Never a 409: the answer names it. */
    baseRevision: z.number().int().positive().optional(),
    candidate: ProjectValidateCandidateSchema.optional(),
    /** Only these providers' pairs; absent = every provider. */
    providers: z.array(RuntimeProviderSchema).min(1).optional(),
    /** Only this runner's pairs; absent = every runner. */
    runner: z.enum(ENGINE_TARGETS).optional(),
});
/** A draft-gate refusal as projects.saveDraft would answer it, with the block its path names. */
export const ProjectValidationErrorSchema = z.object({
    code: z.string(),
    params: z.record(z.string(), z.unknown()).optional(),
    block: BlockKindSchema.optional(),
});
/** The draft gate's answer over the candidate: what a save of it would refuse, in the gate's order. */
export const ProjectValidationDocumentsSchema = z.object({
    ok: z.boolean(),
    errors: z.array(ProjectValidationErrorSchema),
});
/** What a start on one provider and runner would check, over the candidate with its models put on that provider. */
export const ProjectValidationResultSchema = z.object({
    provider: RuntimeProviderSchema,
    runner: z.enum(ENGINE_TARGETS),
    /** The engine a start on the pair meets. */
    engine: z.enum(ENGINES),
    /** Whether this deployment takes a run on the pair now; false = nothing was checked (Foundry's pairs are held). */
    engineInstalled: z.boolean(),
    /**
     * blocked: a start would refuse (a launch blocker, or an enforced fail); degraded: it runs
     * with warnings (advisory fails count here); ok: nothing to say. Null when nothing was checked.
     */
    verdict: z.enum(RUN_PREFLIGHT_VERDICTS).nullable(),
    /** What a start would answer before the engine's checks, in order, with the same params (never mission_required: a start names its mission). */
    blockers: z.array(z.object({ code: z.string(), params: z.record(z.string(), z.unknown()).optional() })),
    findings: z.array(PreflightFindingSchema),
    agents: z.array(PreflightAgentSchema),
    profiles: z.array(PreflightProfileSchema),
    /** Compile warnings, as `<agent>: <code>`. */
    warnings: z.array(z.string()),
    /** What only a real start can tell; on the service runner, that its checks are advisory. */
    unchecked: z.array(PreflightUncheckedSchema),
    /** On the service runner: the runner rule's eligibility and, when not eligible, the shapes the service lacks. */
    serviceEligibility: z.object({ eligible: z.boolean(), reasons: z.array(z.string()) }).optional(),
});
export const ProjectValidationSchema = z.object({
    /** The revision the candidate was built on. */
    baseRevision: z.number().int().positive(),
    checkedAt: z.string().datetime(),
    documents: ProjectValidationDocumentsSchema,
    /** One per provider and runner asked for, in RUNTIME_PROVIDERS × ENGINE_TARGETS order. */
    results: z.array(ProjectValidationResultSchema),
    /** What a run of the candidate needs, read off the root's and every member's agent.yaml. */
    needs: RunNeedsSchema,
});
//# sourceMappingURL=validate.js.map